import * as React from 'react';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Extra strings the search matches against (e.g. aliases). */
  keywords?: string[];
}

/**
 * Substring filter for field-sized comboboxes. cmdk's default is a fuzzy
 * subsequence scorer (fine for a command palette, noisy for value lists:
 * "israel" would match "America/El Salvador"). Every whitespace-separated
 * token must appear verbatim; prefix and word-boundary matches rank first.
 */
export function comboboxFilter(
  value: string,
  search: string,
  keywords?: string[]
): number {
  const needle = search.trim().toLowerCase();
  if (!needle) return 1;
  const haystack = [value, ...(keywords ?? [])].join(' ').toLowerCase();
  const tokens = needle.split(/\s+/);
  if (!tokens.every((token) => haystack.includes(token))) return 0;
  const index = haystack.indexOf(tokens[0]);
  if (index === 0) return 1;
  return /[\s/_-]/.test(haystack[index - 1]) ? 0.9 : 0.7;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
  'aria-label'?: string;
}

/**
 * Single-select searchable dropdown (shadcn combobox: Popover + Command).
 * The trigger mirrors SelectTrigger so combobox and select fields look
 * identical; the list is the shared cmdk styling, compacted via the
 * `combobox-command` scope in soft.css.
 */
function Combobox({
  options,
  value,
  onValueChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyText = 'No results found.',
  id,
  className,
  disabled,
  'aria-label': ariaLabel,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const selected = options.find((option) => option.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          data-slot="combobox-trigger"
          className={cn(
            // Kept in lockstep with SelectTrigger in select.tsx.
            'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground shadow-sm',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 focus:ring-offset-background',
            'disabled:cursor-not-allowed disabled:opacity-50',
            '[&>span]:line-clamp-1',
            className
          )}
        >
          <span className={cn('text-left', !selected && 'text-muted-foreground')}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDownIcon className="h-4 w-4 opacity-50 shrink-0 text-foreground" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[8rem] p-0"
      >
        <Command className="combobox-command" filter={comboboxFilter}>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {options.map((option) => (
              <CommandItem
                key={option.value}
                // The label drives the text filtering; the raw value and any
                // aliases are keywords so "new_york" or "israel" still match.
                value={option.label}
                keywords={[option.value, ...(option.keywords ?? [])]}
                onSelect={() => {
                  onValueChange(option.value);
                  setOpen(false);
                }}
              >
                <span className="cmdk-item-title">{option.label}</span>
                <CheckIcon
                  aria-hidden="true"
                  className={cn(
                    'ml-auto h-4 w-4 shrink-0',
                    option.value === value ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { Combobox };
